import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@workspace/ui/components/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@workspace/ui/components/dropdown-menu'
import { CtaButton } from '@workspace/ui/components/dsm/cta-button'
import { SecondaryButton } from '@workspace/ui/components/dsm/secondary-button'
import { ArrowUpRight, CircleCheck, Copy, Ellipsis, Plus, TriangleAlert } from 'lucide-react'
import { type ReactNode, useState } from 'react'
import { useCopyToClipboard } from '../../../../hooks/use-copy-to-clipboard'
import { trackEvent } from '../../../../lib/gtag'
import { useDisconnectAssistant } from '../../hooks/use-investor-assistant'
import { StartupTile } from '../following/startup-tile'
import { SERVER_URL } from './assistant-guide'

export interface Connection {
  clientId: string
  name: string
  connectedAt: Date | string
  lastUsedAt: Date | string | null
  status: 'CONNECTED' | 'SIGN_IN_AGAIN'
}

interface Workflow {
  id: string
  label: string
  question: string
}

const WORKFLOWS: Workflow[] = [
  {
    id: 'find',
    label: 'Find startups',
    question:
      'Find seed startups in Europe on SeedForge working on climate tech that report revenue.',
  },
  {
    id: 'analyse',
    label: 'Analyse a startup',
    question:
      'Analyse Kelpwise on SeedForge: team, traction, market and the main risks, with the founders’ dated lines.',
  },
  {
    id: 'compare',
    label: 'Compare',
    question:
      'Compare Kelpwise and Ferro on traction and team, and tell me which fits my thesis better.',
  },
  {
    id: 'prepare',
    label: 'Prepare a call',
    question:
      'I meet Kelpwise’s founders next week. What should I ask them, based on the gaps in what they published?',
  },
  {
    id: 'watch',
    label: 'Keep watch',
    question: 'Follow Kelpwise on SeedForge and tell me when they sign their first paid contract.',
  },
  {
    id: 'catch-up',
    label: 'Catch up',
    question: 'What changed at the startups I follow on SeedForge since my last session?',
  },
]

/** Where each assistant is opened to sign in again (not every assistant has a page we know). */
const ASSISTANT_HOMES: { match: RegExp; href: string }[] = [
  { match: /claude/i, href: 'https://claude.ai' },
  { match: /chatgpt|openai/i, href: 'https://chatgpt.com' },
  { match: /gemini/i, href: 'https://gemini.google.com' },
  { match: /perplexity/i, href: 'https://www.perplexity.ai' },
  { match: /le chat|mistral/i, href: 'https://chat.mistral.ai' },
  { match: /copilot/i, href: 'https://copilot.microsoft.com' },
]

const formatDay = (value: Date | string) =>
  new Date(value).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })

function lastUsedText(connection: Connection): string {
  if (!connection.lastUsedAt) return 'not used yet'
  const used = new Date(connection.lastUsedAt)
  if (used.toDateString() === new Date().toDateString()) {
    return `last used today at ${used.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`
  }
  return `last used ${formatDay(used)}`
}

/** The assistant used most recently: the one a question should open. */
function mostRecentlyUsed(connections: [Connection, ...Connection[]]): Connection {
  const usedAt = (c: Connection) => new Date(c.lastUsedAt ?? c.connectedAt).getTime()
  return connections.reduce((latest, c) => (usedAt(c) > usedAt(latest) ? c : latest))
}

/** Only Claude has a question link that opens a new chat with the text ready; others copy it. */
function claudeLink(question: string): string {
  return `https://claude.ai/new?q=${encodeURIComponent(question)}`
}

function AskRow({
  workflow,
  assistant,
  primary,
}: {
  workflow: Workflow
  assistant: Connection
  /** The first row is the page's one orange action; the rest are quiet links. */
  primary: boolean
}) {
  const { copied, copy } = useCopyToClipboard()
  const opensClaude = /claude/i.test(assistant.name)
  const run = () => {
    trackEvent('assistant_ask_clicked', {
      workflow: workflow.id,
      mode: opensClaude ? 'open' : 'copy',
    })
    if (opensClaude) {
      window.open(claudeLink(workflow.question), '_blank', 'noopener,noreferrer')
    } else {
      copy(workflow.question)
    }
  }
  const label = opensClaude ? 'Ask in Claude' : copied ? 'Copied' : 'Copy question'
  return (
    <li className="grid grid-cols-1 gap-2 py-4 md:grid-cols-[10rem_1fr_auto] md:gap-6">
      <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
        {workflow.label}
      </p>
      <div>
        <p className="text-sm text-foreground">“{workflow.question}”</p>
      </div>
      {primary ? (
        <CtaButton
          size="sm"
          icon={opensClaude ? <ArrowUpRight className="size-3.5" /> : undefined}
          onClick={run}
        >
          {label}
        </CtaButton>
      ) : (
        <button
          type="button"
          onClick={run}
          className="inline-flex items-center gap-1 self-start text-sm text-muted-foreground hover:text-foreground"
        >
          {label}
          {opensClaude && <ArrowUpRight className="size-3.5" />}
        </button>
      )}
    </li>
  )
}

function SignInAgainBanner({ connection }: { connection: Connection }) {
  const home = ASSISTANT_HOMES.find((h) => h.match.test(connection.name))?.href
  return (
    <div
      role="alert"
      className="flex flex-wrap items-center gap-4 rounded-xl border border-amber-500/30 bg-amber-500/10 px-5 py-4"
    >
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-foreground">
          {connection.name} needs you to sign in again
        </p>
        <p className="mt-0.5 text-sm text-muted-foreground">
          Open SeedForge in {connection.name}, choose Connect and sign in with your SeedForge
          account.
        </p>
      </div>
      {home && (
        <CtaButton
          size="sm"
          icon={<ArrowUpRight className="size-3.5" />}
          onClick={() => {
            trackEvent('assistant_open_clicked', { assistant: connection.name })
            window.open(home, '_blank', 'noopener,noreferrer')
          }}
        >
          Open {connection.name}
        </CtaButton>
      )}
    </div>
  )
}

function AssistantRow({ connection }: { connection: Connection }) {
  const disconnect = useDisconnectAssistant()
  const [asking, setAsking] = useState(false)
  return (
    <li className="flex items-center gap-4 px-5 py-4">
      <StartupTile name={connection.name} />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-foreground">{connection.name}</p>
        <p className="text-xs text-muted-foreground">
          Connected {formatDay(connection.connectedAt)} · {lastUsedText(connection)}
        </p>
      </div>
      {connection.status === 'SIGN_IN_AGAIN' ? (
        <span className="inline-flex items-center gap-1.5 text-sm font-medium text-amber-500">
          <TriangleAlert className="size-4" />
          Sign in again
        </span>
      ) : (
        <span className="inline-flex items-center gap-1.5 text-sm font-medium text-emerald-500">
          <CircleCheck className="size-4" />
          Connected
        </span>
      )}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label={`${connection.name} options`}
            className="flex size-8 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <Ellipsis className="size-4" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem variant="destructive" onSelect={() => setAsking(true)}>
            Disconnect
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={asking} onOpenChange={setAsking}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Disconnect {connection.name}?</DialogTitle>
            <DialogDescription>
              {connection.name} will need a new sign-in to use SeedForge.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose asChild>
              <SecondaryButton size="sm">Cancel</SecondaryButton>
            </DialogClose>
            <SecondaryButton
              size="sm"
              tone="danger"
              disabled={disconnect.isPending}
              onClick={() => {
                trackEvent('assistant_disconnected', { assistant: connection.name })
                disconnect.mutate(
                  { path: { clientId: connection.clientId } },
                  { onSuccess: () => setAsking(false) }
                )
              }}
            >
              Disconnect
            </SecondaryButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </li>
  )
}

export function ConnectedPanel({
  connections,
  setupGuide,
  checkRunning,
}: {
  connections: [Connection, ...Connection[]]
  /** The investor account check is still running: the AI can search, so only the search example shows. */
  checkRunning: boolean
  /** The "Set it up in" guide, shown right under "Add another assistant" when asked for. */
  setupGuide: ReactNode
}) {
  const [showSetup, setShowSetup] = useState(false)
  const assistant = mostRecentlyUsed(connections)
  const { copied, copy } = useCopyToClipboard()
  const workflows = checkRunning ? WORKFLOWS.filter((w) => w.id === 'find') : WORKFLOWS
  return (
    <div className="space-y-8">
      {connections
        .filter((c) => c.status === 'SIGN_IN_AGAIN')
        .map((c) => (
          <SignInAgainBanner key={c.clientId} connection={c} />
        ))}
      <section className="rounded-xl border border-border">
        <div className="border-b border-border px-5 py-4">
          <h2 className="text-sm font-semibold text-foreground">Ask in {assistant.name}</h2>
          <p className="mt-1 text-xs text-muted-foreground">
            {/claude/i.test(assistant.name)
              ? 'Each opens Claude with the question ready. You press send. Change the names and numbers to yours.'
              : 'Copy a question, paste it into your assistant and press send. Change the names and numbers to yours.'}
          </p>
        </div>
        <ul className="divide-y divide-border px-5">
          {workflows.map((workflow, i) => (
            <AskRow key={workflow.id} workflow={workflow} assistant={assistant} primary={i === 0} />
          ))}
        </ul>
      </section>

      <section className="space-y-3">
        <h2 className="text-sm font-semibold text-foreground">Your assistants</h2>
        <div className="rounded-xl border border-border">
          <ul className="divide-y divide-border">
            {connections.map((connection) => (
              <AssistantRow key={connection.clientId} connection={connection} />
            ))}
          </ul>
          <div className="flex flex-wrap items-center gap-4 border-t border-border px-5 py-4">
            <span className="flex size-10 shrink-0 items-center justify-center rounded-lg border border-dashed border-border text-muted-foreground">
              <Plus className="size-4" />
            </span>
            <div className="min-w-0 flex-1">
              <h3 className="text-sm font-medium text-foreground">Add another assistant</h3>
              <p className="truncate text-xs text-muted-foreground">{SERVER_URL}</p>
            </div>
            <SecondaryButton
              size="sm"
              icon={<Copy className="size-3.5" />}
              onClick={() => {
                copy(SERVER_URL)
                trackEvent('assistant_setup_copied', { target: 'server_address' })
              }}
            >
              {copied ? 'Copied' : 'Copy address'}
            </SecondaryButton>
            <SecondaryButton
              size="sm"
              aria-expanded={showSetup}
              onClick={() => setShowSetup((open) => !open)}
            >
              {showSetup ? 'Hide steps' : 'Step by step'}
            </SecondaryButton>
          </div>
          {showSetup && <div className="border-t border-border p-5">{setupGuide}</div>}
        </div>
      </section>
    </div>
  )
}
